export function formatMoney(pricing = {}) {
  const amount = (pricing.amountCents || 0) / 100;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: pricing.currency || "usd"
  }).format(amount);
}

export function formatDateOnly(value, fallback = "Not set") {
  if (!value) {
    return fallback;
  }
  return new Date(value).toLocaleDateString([], {
    dateStyle: "medium",
    timeZone: "UTC"
  });
}

export function isLibertyLongRange(range = {}) {
  const text = [
    range?.name,
    range?.slug,
    range?.profile?.displayName,
    range?.profile?.website
  ].filter(Boolean).join(" ").toLowerCase();
  return (
    text.includes("liberty long range") ||
    text.includes("libertylongrange.com") ||
    text.includes("e2e-stripe-components-202605171803")
  );
}

export function publicDisplayName(range = {}) {
  return isLibertyLongRange(range) ? "Liberty Long Range" : range?.name || "Range membership";
}

export function workspaceTemplateKey(range = {}) {
  return String(range?.config?.templateKey || range?.templateKey || "physical_range").trim().toLowerCase();
}

export function workspaceVocabulary(range = {}) {
  return range?.config?.vocabulary || {};
}

export function workspaceNoun(range = {}, fallback = "range") {
  return workspaceVocabulary(range).workspace?.toLowerCase() || fallback;
}

export function workspaceMemberNoun(range = {}, fallback = "member") {
  return workspaceVocabulary(range).member?.toLowerCase() || fallback;
}

export function workspaceEventNounPlural(range = {}, fallback = "events") {
  const event = workspaceVocabulary(range).event || "";
  if (event) {
    const lower = event.toLowerCase();
    if (lower.endsWith("s")) {
      return `${lower}es`;
    }
    if (lower.endsWith("y")) {
      return `${lower.slice(0, -1)}ies`;
    }
    return `${lower}s`;
  }
  return fallback;
}

export function isChapterOrganization(range = {}) {
  const unit = workspaceVocabulary(range).unit || "";
  return workspaceTemplateKey(range) === "chapter_organization" || String(unit).toLowerCase() === "chapter";
}

export function publicChapterMembershipHref(slug, chapter) {
  const params = new URLSearchParams();
  if (chapter?.chapterId) {
    params.set("chapterId", chapter.chapterId);
  }
  return `/r/${encodeURIComponent(slug)}/membership${params.toString() ? `?${params.toString()}` : ""}`;
}

export function publicChapterLabel(chapter = {}) {
  return [chapter.name, [chapter.city, chapter.region].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" / ");
}

export function formatAddress(address = {}) {
  return [
    address.line1,
    address.line2,
    [address.city, address.region, address.postalCode].filter(Boolean).join(", "),
    address.country
  ].filter(Boolean).join(" / ");
}

export function publicContactLooksInternal(value = "") {
  return /(?:@dev\.rangegrid\.local|rangegrid-.*audit|owner-audit|test@|example\.com|555[-\s.]?0|555[-\s.]?0147)/i.test(String(value || ""));
}

export function publicAddressLooksInternal(value = "") {
  return /(?:100\s+Chapter\s+House\s+Way|Los Angeles,\s*CA,\s*90012|Example|Demo|Test)/i.test(String(value || ""));
}

export function publicMemberSupportContact(range = {}) {
  const profile = range?.profile || {};
  const fallback = isChapterOrganization(range) && /lox\s*&\s*loaded/i.test(publicDisplayName(range) || "")
    ? {
        email: "info@loxandloaded.org",
        phone: "1 (334) 569-5623"
      }
    : {};
  const email = profile.contactEmail && !publicContactLooksInternal(profile.contactEmail)
    ? profile.contactEmail
    : fallback.email || "";
  const phone = profile.contactPhone && !publicContactLooksInternal(profile.contactPhone)
    ? profile.contactPhone
    : fallback.phone || "";
  return { email, phone };
}

export function publicEventTimezone(event = {}, range = {}) {
  const explicitTimezone = event.timezone || event.timeZone || event.chapter?.timezone || event.chapter?.timeZone;
  if (explicitTimezone) {
    return explicitTimezone;
  }
  const text = [
    event.title,
    event.location,
    event.chapterName,
    event.chapter?.name,
    event.chapter?.city,
    event.chapter?.state,
    event.chapter?.region,
    range?.name,
    range?.slug
  ].filter(Boolean).join(" ");
  if (isChapterOrganization(range) && /\b(saint paul|st\.?\s*paul|twin cities|minneapolis|minnesota|mn)\b/i.test(text)) {
    return "America/Chicago";
  }
  return "";
}

export function formatPublicEventDate(value, timezone = "") {
  if (!value) {
    return "";
  }
  if (timezone) {
    return new Date(value).toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
      timeZoneName: "short"
    });
  }
  const options = {
    dateStyle: "medium",
    timeStyle: "short"
  };
  return new Date(value).toLocaleString([], options);
}

export function defaultLeadForm() {
  return {
    name: "",
    email: "",
    phone: "",
    interest: "opening_updates",
    message: ""
  };
}

function attributionValue(value, maxLength = 120) {
  return String(value || "").trim().slice(0, maxLength);
}

export function publicCampaignAttribution(range) {
  const defaultSource = isLibertyLongRange(range) ? "liberty_public_presale" : "public_range_page";
  if (typeof window === "undefined") {
    return { source: defaultSource };
  }

  const params = new URLSearchParams(window.location.search || "");
  const campaign = {
    code: attributionValue(params.get("campaign_code") || params.get("campaignCode"), 80),
    name: attributionValue(params.get("campaign") || params.get("utm_campaign")),
    medium: attributionValue(params.get("utm_medium"), 80),
    content: attributionValue(params.get("utm_content"))
  };
  Object.keys(campaign).forEach((key) => {
    if (!campaign[key]) {
      delete campaign[key];
    }
  });

  return {
    source: attributionValue(params.get("source") || params.get("utm_source"), 80) || defaultSource,
    ...(Object.keys(campaign).length > 0 ? { campaign } : {})
  };
}

export function campaignAttributionLabel(attribution = {}) {
  if (!attribution.campaign) {
    return "";
  }
  return [
    attribution.campaign.name || attribution.campaign.code,
    attribution.source && attribution.source !== "public_range_page" ? attribution.source.replaceAll("_", " ") : ""
  ].filter(Boolean).join(" / ");
}
